import { useMutation, useQueryClient } from "@tanstack/react-query";
import { vaultKeys } from "../queryKeys";
import { bdcApi, type BdcStateResponse } from "../../../api/bdc.api";
import { useAppSelector } from "../../../redux/hooks";
import { getVaultActorAuthScope } from "../../../utils/vault/vaultActorSession";

interface BdcConfirmPinParams {
  reqId: string;
  pin: string;
  confirmPin: string;
}

/**
 * POST bdc/confirm-pin — confirma el NIP. En éxito (`status:"completed"`) el
 * backend marca el requirement `bdc-authorization` como Valid.
 *
 * En éxito INVALIDA el query del folder/requirement para que el vault recalcule
 * y muestre la tarjeta de completado, y refresca la cache de `bdc/status`.
 *
 * Defensa R10: si el backend devolviera un 200 con un `code` de error en el
 * body (y `status` != "completed"), se lanza como error para que el componente
 * lo trate con `mapBdcAxiosError`. NO swallowea errores: el componente necesita
 * el code (1009 decrementa retry, 1013 ofrece reenvío, etc.).
 */
export function useBdcConfirmPin(folderId?: string) {
  const queryClient = useQueryClient();
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);
  const authScope = getVaultActorAuthScope(jwtToken);

  return useMutation<BdcStateResponse, unknown, BdcConfirmPinParams>({
    mutationFn: async ({ reqId, pin, confirmPin }) => {
      const data = await bdcApi.confirmPin(reqId, {
        pin,
        confirm_pin: confirmPin,
      });
      // 200 con error en el body -> tratar como error (catálogo de codes).
      if (data?.code && data.status !== "completed") {
        throw { response: { data } };
      }
      return data;
    },
    onMutate: async ({ reqId }) => {
      await queryClient.cancelQueries({
        queryKey: vaultKeys.bdcStatus(reqId, authScope),
      });
    },
    onSuccess: async (data, { reqId }) => {
      const queryKey = vaultKeys.bdcStatus(reqId, authScope);
      // Reconnection can start another GET while the POST is in flight.
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(
        queryKey,
        (prev: BdcStateResponse | undefined) => ({
          ...prev,
          ...data,
          // The mutation may change the local verdict. Keep source metadata,
          // but do not carry the preceding GET's obsolete requirement status.
          requirement_status: data.requirement_status,
        }),
      );
      await queryClient.invalidateQueries({
        queryKey: vaultKeys.folder(folderId ?? ""),
      });
    },
  });
}
